<template>
  <div class="mt-3">
    <AdvisoryRadioGroup
      :model-value="hasExistingInsurance"
      name="hasExistingInsurance"
      description="Disposez-vous déjà d'une assurance vous couvrant pour les mêmes risques ?"
      :options="hasExistingInsuranceOptions"
      translation-prefix="hasExistingInsurance"
      @update:model-value="$emit('update:hasExistingInsurance', $event)"
    >
      <template #label>
        <span class="flex items-center gap-2">
          <span>Existence d'un cumul d'assurance ?</span>
          <button
            type="button"
            class="text-xs font-normal text-primary-dark hover:text-primary underline focus:outline-hidden focus:ring-2 focus:ring-primary focus:ring-offset-1 rounded-sm"
            @click="$emit('showCumulModal')"
          >
            En savoir plus
          </button>
        </span>
      </template>
    </AdvisoryRadioGroup>

    <template v-if="hasExistingInsurance === true">
      <div class="mt-2">
        <label class="block text-sm font-medium text-gray-700">
          Pourquoi souhaitez-vous souscrire une nouvelle assurance ?
          <span class="text-red-600">*</span>
        </label>
        <Listbox
          :model-value="existingInsuranceReason"
          as="div"
          class="relative mt-0.5"
          @update:model-value="$emit('update:existingInsuranceReason', $event)"
        >
          <ListboxButton
            class="relative w-full cursor-pointer rounded-md border border-gray-300 bg-white py-1.5 pl-2.5 pr-9 text-left shadow-xs focus:border-primary focus:outline-hidden focus:ring-1 focus:ring-primary text-sm"
          >
            <span class="block truncate">
              {{ existingInsuranceReasonLabel || "Sélectionner une raison..." }}
            </span>
            <span class="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
              <ChevronDownIcon class="h-4 w-4 text-gray-400" aria-hidden="true" />
            </span>
          </ListboxButton>
          <ListboxOptions
            class="absolute z-10 mt-0.5 max-h-48 w-full overflow-auto rounded-md bg-white py-0.5 text-sm shadow-lg ring-1 ring-black/5 focus:outline-hidden"
          >
            <ListboxOption
              v-for="opt in existingInsuranceReasonOptions"
              :key="opt.value"
              v-slot="{ active, selected }"
              :value="opt.value"
              as="template"
            >
              <li
                :class="[
                  active ? 'bg-primary-light text-gray-900' : 'text-gray-900',
                  'relative cursor-pointer select-none py-1.5 pl-2.5 pr-8 text-sm',
                ]"
              >
                <span :class="[selected ? 'font-semibold' : 'font-normal', 'block truncate']">
                  {{ $t(`advisory.existingInsuranceReason.${opt.value}`) }}
                </span>
                <span v-if="selected" class="absolute inset-y-0 right-0 flex items-center pr-4 text-primary-dark">
                  ✓
                </span>
              </li>
            </ListboxOption>
          </ListboxOptions>
        </Listbox>
        <div v-if="existingInsuranceReason === 'other'" class="mt-2">
          <label for="existingInsuranceReasonOtherText" class="block text-sm font-medium text-gray-700">
            Précisez votre raison
            <span class="text-red-600">*</span>
          </label>
          <input
            id="existingInsuranceReasonOtherText"
            :value="existingInsuranceReasonOtherText"
            type="text"
            class="mt-0.5 block w-full rounded-md border border-gray-300 px-2.5 py-1.5 text-sm shadow-xs focus:border-primary focus:ring-1 focus:ring-primary"
            maxlength="500"
            @input="$emit('update:existingInsuranceReasonOtherText', $event.target.value)"
          />
          <ErrorMessage name="existingInsuranceReasonOtherText" class="mt-1 text-xs text-red-600" />
        </div>
        <ErrorMessage name="existingInsuranceReason" class="mt-1 text-xs text-red-600" />
      </div>
      <div class="mt-2 p-2 rounded-md bg-yellow-50 border border-yellow-200 text-xs text-yellow-800">
        <p class="font-semibold">⚠️ Point important : cumul d'assurance déclaré</p>
        <p class="mt-1">
          <strong>En cas de souscription d'un nouveau contrat :</strong>
          Vous devez informer chaque assureur de l'existence de l'autre contrat et du niveau de garantie associé.
        </p>
        <p class="mt-1">
          <strong>En cas de sinistre :</strong>
          Le total des indemnisations ne pourra jamais dépasser la valeur de l'appareil assuré.
        </p>
      </div>
    </template>
  </div>
</template>

<script>
import { Listbox, ListboxButton, ListboxOption, ListboxOptions } from "@headlessui/vue";
import { ErrorMessage } from "vee-validate";
import { ChevronDownIcon } from "@heroicons/vue/outline";
import AdvisoryRadioGroup from "@/components/pos/subscription/formwithsteps/AdvisoryRadioGroup.vue";
import {
  HAS_EXISTING_INSURANCE_OPTIONS,
  EXISTING_INSURANCE_REASON_OPTIONS,
} from "@/components/pos/subscription/formwithsteps/advisoryFormConfig";

export default {
  components: {
    Listbox,
    ListboxButton,
    ListboxOption,
    ListboxOptions,
    ErrorMessage,
    ChevronDownIcon,
    AdvisoryRadioGroup,
  },
  props: {
    hasExistingInsurance: { type: [Boolean], default: undefined },
    existingInsuranceReason: { type: String, default: "" },
    existingInsuranceReasonOtherText: { type: String, default: "" },
  },
  emits: [
    "update:hasExistingInsurance",
    "update:existingInsuranceReason",
    "update:existingInsuranceReasonOtherText",
    "showCumulModal",
  ],
  data() {
    return {
      hasExistingInsuranceOptions: HAS_EXISTING_INSURANCE_OPTIONS,
      existingInsuranceReasonOptions: EXISTING_INSURANCE_REASON_OPTIONS,
    };
  },
  computed: {
    existingInsuranceReasonLabel() {
      const opt = this.existingInsuranceReasonOptions.find((o) => o.value === this.existingInsuranceReason);
      return opt ? this.$t(`advisory.existingInsuranceReason.${opt.value}`) : "";
    },
  },
};
</script>
